import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useBranch } from '@/context/BranchContext';

/**
 * Switcher integrado de sucursal/cuenta para el header.
 * - Administrador: puede cambiar entre sucursales y "Todas las sucursales".
 * - Cajero: ve solo su sucursal asignada (no interactivo).
 * Estilo glass/frosted que respeta el modo claro y oscuro.
 */
export default function BranchSwitcher() {
  const { isAdmin } = useAuth();
  const { branches, activeBranchId, activeBranch, setActiveBranchId } = useBranch();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const label = activeBranch?.name ?? (isAdmin ? 'Todas las sucursales' : 'Sin sucursal');

  const shellClass =
    'flex items-center gap-2 rounded-full border border-background-200/70 bg-background-50/70 backdrop-blur-md px-2.5 py-1.5 min-w-0';

  if (!isAdmin) {
    return (
      <div className={shellClass}>
        {activeBranch?.logo_url ? (
          <img
            src={activeBranch.logo_url}
            alt={label}
            className="w-7 h-7 rounded-full object-contain bg-background-100 shrink-0"
          />
        ) : (
          <span className="w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
            <i className="ri-store-2-line text-sm" />
          </span>
        )}
        <span className="truncate max-w-[140px] text-sm font-semibold text-foreground-950">{label}</span>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`${shellClass} max-w-full cursor-pointer hover:bg-background-100/70 transition`}
        title="Cambiar sucursal"
      >
        {activeBranch?.logo_url ? (
          <img
            src={activeBranch.logo_url}
            alt={label}
            className="w-7 h-7 rounded-full object-contain bg-background-100 shrink-0"
          />
        ) : (
          <span className="w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
            <i className="ri-store-2-line text-sm" />
          </span>
        )}
        <span className="truncate max-w-[150px] text-sm font-semibold text-foreground-950">{label}</span>
        <i
          className={`ri-arrow-down-s-line text-foreground-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-50 w-64 rounded-xl border border-background-200/70 bg-background-50/95 backdrop-blur-md p-1.5">
          <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
            Sucursal
          </p>
          <button
            onClick={() => {
              setActiveBranchId(null);
              setOpen(false);
            }}
            className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition cursor-pointer ${
              !activeBranchId
                ? 'bg-primary-50 text-primary-700'
                : 'text-foreground-700 hover:bg-background-100'
            }`}
          >
            <i className="ri-global-line" /> Todas las sucursales
            {!activeBranchId && <i className="ri-check-line ml-auto" />}
          </button>
          {branches.map((b) => (
            <button
              key={b.id}
              onClick={() => {
                setActiveBranchId(b.id);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition cursor-pointer ${
                activeBranchId === b.id
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i className="ri-store-2-line" />
              <span className="truncate">{b.name}</span>
              {activeBranchId === b.id && <i className="ri-check-line ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}